(() => {
  if (customElements.get('merchiq-quote-request')) return;
  const apparelServices = new Set(['Screen Printing','Embroidery','DTF Transfers','Gang Sheet','Full Service Merch Program','Not sure']);
  const setValue = (target, path, value) => {
    const parts = path.split('.');
    if (parts.length === 1) target[path] = value;
    else { target[parts[0]] ||= {}; target[parts[0]][parts[1]] = value; }
  };
  const getValue = (target, path) => path.split('.').reduce((value,key) => value?.[key],target);
  let turnstileReady;
  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!turnstileReady) turnstileReady = new Promise((resolve,reject) => {
      let script = [...document.querySelectorAll('script[src^="https://challenges.cloudflare.com/turnstile/v0/api.js"]')]
        .find(node => !node.dataset.merchiqTurnstileFailed);
      let owned = false;
      const failed = () => {
        clearTimeout(timeout);
        script?.removeEventListener('load', loaded);
        script?.removeEventListener('error', failed);
        if (script) {
          script.dataset.merchiqTurnstileFailed = 'true';
          if (owned) script.remove();
        }
        reject(new Error('Security check unavailable'));
      };
      const loaded = () => { clearTimeout(timeout); window.turnstile ? resolve(window.turnstile) : failed(); };
      const timeout = setTimeout(failed,15000);
      if (!script) {
        script = document.createElement('script');
        owned = true;
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true; script.defer = true;
        script.addEventListener('load', loaded, {once:true});
        script.addEventListener('error', failed, {once:true});
        document.head.appendChild(script);
      } else {
        script.addEventListener('load', loaded, {once:true});
        script.addEventListener('error', failed, {once:true});
      }
    }).catch(error => { turnstileReady = undefined; throw error; });
    return turnstileReady;
  }

  customElements.define('merchiq-quote-request', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.status = this.querySelector('[data-status]');
      this.retry = this.querySelector('[data-retry]');
      this.security = this.querySelector('[data-security]');
      this.securityStatus = this.querySelector('[data-security-status]');
      this.securityRetry = this.querySelector('[data-security-retry]');
      this.contactActions = this.querySelector('[data-contact-actions]');
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.fields = [...this.querySelectorAll('[data-field]')];
      this.step = 0; this.pending = null; this.busy = false; this.token = '';
      this.storageKey = `merchiq.request.${this.dataset.storageKey}`;
      // The form stays hidden until this handler is installed. Without the
      // enhancement, visitors use the visible shop contact options instead.
      this.form.addEventListener('submit', event => { event.preventDefault(); this.submit(); });
      this.form.noValidate = true;
      try {
        const endpoint = new URL(this.dataset.endpoint);
        if (endpoint.protocol !== 'https:' || endpoint.username || endpoint.password || !this.dataset.sitekey) throw new Error('configuration');
      } catch (_) {
        this.querySelector('[data-submit]').disabled = true;
        this.message('This request form is not available yet. Please use our existing contact options.',true);
        return;
      }
      this.form.hidden = false;
      this.querySelector('[data-unavailable]').hidden = true;
      this.querySelector('[data-primary-service]').value = this.dataset.defaultService || '';
      this.classList.add('is-enhanced');
      this.querySelectorAll('[data-next],[data-back]').forEach(button => {
        button.hidden = false;
        button.addEventListener('click', () => {
          if (button.hasAttribute('data-next') && !this.validStep()) return;
          this.showStep(this.step + (button.hasAttribute('data-next') ? 1 : -1));
        });
      });
      this.form.addEventListener('change', () => this.conditionals());
      this.retry.addEventListener('click', () => this.submit());
      this.securityRetry.addEventListener('click', () => this.securityCheck(true));
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey));
        if (saved && typeof saved.submission_id === 'string' && saved.payload && typeof saved.payload === 'object') this.pending = saved;
      } catch (_) { /* Storage can be unavailable in a private browser. */ }
      if (this.pending) {
        this.querySelector('[data-name-part="first"]').value = this.pending.contact_names?.first || this.pending.payload.name || '';
        this.querySelector('[data-name-part="last"]').value = this.pending.contact_names?.last || '';
        this.fields.forEach(input => {
          const value = getValue(this.pending.payload,input.dataset.field);
          if (input.type === 'checkbox') input.checked = value === true;
          else if (value != null) input.value = value;
        });
        this.querySelector('[data-primary-service]').value = this.pending.payload.services?.[0] || '';
        this.querySelectorAll('[data-service]').forEach(input => {input.checked = this.pending.payload.services?.slice(1).includes(input.value) || false;});
        this.showStep(2,false); this.lock(true); this.retry.hidden = false;
        this.message('Your last request still needs confirmation. Retry the saved request to recover its receipt.');
      } else this.showStep(0,false);
      this.conditionals();
    }

    services() {
      return [...new Set([this.querySelector('[data-primary-service]').value,...[...this.querySelectorAll('[data-service]:checked')].map(input=>input.value)].filter(Boolean))];
    }

    conditionals() {
      const chosen = this.services();
      const apparel = chosen.length === 0 || chosen.some(value => apparelServices.has(value));
      this.querySelectorAll('[data-apparel]').forEach(node => {node.hidden = !apparel;});
      this.querySelector('[data-shipping]').hidden = this.querySelector('[data-field="fulfillment.method"]').value !== 'shipping';
    }

    showStep(index, focus = true) {
      this.step = Math.max(0,Math.min(index,2));
      this.steps.forEach((node,i) => {node.hidden = i !== this.step;});
      this.querySelectorAll('[data-step-label]').forEach((node,i) => {
        if (i === this.step) node.setAttribute('aria-current','step'); else node.removeAttribute('aria-current');
      });
      if (focus) {
        const legend = this.steps[this.step].querySelector('legend');
        legend.tabIndex = -1; legend.focus();
      }
      this.security.hidden = this.step !== 2;
      this.contactActions.hidden = this.step !== 2;
      if (this.step === 2) this.securityCheck();
    }

    validStep() {
      this.steps[this.step].querySelectorAll('[data-name-part]').forEach(input => {
        input.setCustomValidity(input.value.trim() ? '' : `Enter your ${input.dataset.namePart} name.`);
      });
      if (this.step === 0 && !this.querySelector('[data-primary-service]').value) {
        this.message('Choose at least one service, or select Not sure.',true);
        this.showStep(0,false); this.querySelector('[data-primary-service]').focus(); return false;
      }
      const invalid = [...this.steps[this.step].querySelectorAll('input,select,textarea')].find(input => !input.checkValidity());
      if (invalid) { this.showStep(this.step,false); invalid.reportValidity(); return false; }
      this.message(''); return true;
    }

    payload() {
      const payload = {services:this.services(),sms_consent:false};
      for (const input of this.fields) {
        if (input.closest('[data-apparel]')?.hidden || input.closest('[data-shipping]')?.hidden) continue;
        let value = input.type === 'checkbox' ? input.checked : input.value.trim();
        if (input.dataset.field === 'quantity' && !value) value = null;
        if (input.type === 'date' && !value) value = null;
        setValue(payload,input.dataset.field,value);
      }
      payload.name = [this.querySelector('[data-name-part="first"]').value.trim(),this.querySelector('[data-name-part="last"]').value.trim()].join(' ');
      return payload;
    }

    securityMessage(text, error = false) {
      if (this.complete) return;
      this.securityStatus.textContent = text;
      this.securityStatus.dataset.error = String(error);
      this.securityRetry.hidden = Boolean(this.token);
      this.querySelector('[data-security-help]').hidden = !error;
    }

    async securityCheck(restart = false) {
      if (this.complete || this.loadingWidget || (this.widget !== undefined && !restart)) return;
      this.loadingWidget = true;
      this.securityRetry.disabled = true;
      this.token = '';
      const attempt = this.securityAttempt = (this.securityAttempt || 0) + 1;
      clearTimeout(this.securityTimeout);
      const current = () => !this.complete && this.securityAttempt === attempt;
      const failed = () => {
        if (!current()) return;
        clearTimeout(this.securityTimeout);
        this.token = '';
        this.securityMessage('The security check could not load. Reload it below to try again.', true);
      };
      this.securityMessage('Checking your browser. If nothing appears, reload the security check.');
      this.securityTimeout = setTimeout(() => {
        if (!current()) return;
        this.token = '';
        this.securityMessage('The security check did not finish. Reload it below to try again.',true);
      },20000);
      try {
        const turnstile = await loadTurnstile();
        if (!current()) return;
        if (this.widget !== undefined) {
          turnstile.remove(this.widget);
          this.widget = undefined;
        }
        this.widget = turnstile.render(this.querySelector('[data-turnstile]'), {
          sitekey:this.dataset.sitekey, action:'quote_request',
          callback:token => {
            if (!current()) return;
            clearTimeout(this.securityTimeout);
            this.token = token;
            if (this.waitingForSecurity && !this.busy) {
              this.message('');
              this.waitingForSecurity = false;
            }
            this.securityMessage(this.pending ? 'Security check complete. Retry your saved request.' : 'Security check complete. You can send your request.');
          },
          'expired-callback':() => {
            if (!current()) return;
            clearTimeout(this.securityTimeout);
            this.token = '';
            this.securityMessage('The security check expired. Reload it below to continue.',true);
          },
          'timeout-callback':failed,
          'error-callback':failed
        });
      } catch (_) { failed(); }
      finally {this.loadingWidget = false;this.securityRetry.disabled = false;}
    }

    message(text,error=false) {this.status.textContent=text;this.status.dataset.error=String(error);}
    lock(locked) {
      this.steps.forEach(node=>{node.disabled=locked;});
      this.contactActions.querySelectorAll('button').forEach(button=>{button.disabled=locked;});
      this.classList.toggle('is-pending',locked);
    }
    forgetPending() {this.pending=null;try{sessionStorage.removeItem(this.storageKey);}catch(_) {}}

    async submit() {
      if (this.busy || this.complete) return;
      if (!this.pending) {
        for (let step=0;step<3;step++) {
          this.step=step;
          if (!this.validStep()) {this.showStep(step,false);return;}
        }
        this.showStep(2,false);
        if (!this.token) {
          this.waitingForSecurity = true;
          this.message('Your request has not been sent. Use the security check above to continue.',true);
          this.security.scrollIntoView({block:'nearest'});
          return;
        }
        this.pending={submission_id:crypto.randomUUID(),payload:this.payload(),contact_names:{
          first:this.querySelector('[data-name-part="first"]').value.trim(),last:this.querySelector('[data-name-part="last"]').value.trim()
        }};
        try {sessionStorage.setItem(this.storageKey,JSON.stringify(this.pending));} catch (_) {}
      }
      if (this.pending.challenge_required && !this.token) {
        this.waitingForSecurity = true;
        this.message('Your details are saved. Use the security check above, then retry your saved request.',true);
        this.security.scrollIntoView({block:'nearest'});
        return;
      }
      // A fresh attempt can commit even if its response is lost. Keep receipt
      // recovery available; the server will request another check if needed.
      delete this.pending.challenge_required;
      try {sessionStorage.setItem(this.storageKey,JSON.stringify(this.pending));} catch (_) {}
      this.waitingForSecurity=false;
      this.busy=true;this.lock(true);this.retry.hidden=true;this.message('Saving your request. Please keep this page open.');
      const controller = new AbortController();
      const timeout = setTimeout(()=>controller.abort(),35000);
      try {
        const result = await fetch(this.dataset.endpoint,{method:'POST',headers:{'Content-Type':'application/json'},
          signal:controller.signal,body:JSON.stringify({submission_id:this.pending.submission_id,payload:this.pending.payload,turnstile_token:this.token,website_url:this.form.elements.website_url.value})});
        const data = await result.json();
        if (!result.ok || data.ok !== true || !/^(?:RQ-\d{4,12}|QR-\d{5,})$/.test(data.reference || '') || typeof data.receipt_id !== 'string') {
          if (data.challenge_required) {
            this.pending.challenge_required = true;
            try {sessionStorage.setItem(this.storageKey,JSON.stringify(this.pending));} catch (_) {}
            this.token='';
            this.securityCheck(true);
            throw new Error('Your details are saved. Use the security check above, then retry your saved request.');
          }
          if (result.status===422) {this.forgetPending();this.lock(false);}
          throw new Error(data.error || 'We could not confirm your request. Please retry.');
        }
        this.complete=true;this.forgetPending();
        clearTimeout(this.securityTimeout);this.security.hidden=true;
        this.message(`Request received: ${data.reference}. Our team will review your project and follow up by email. You can add artwork using the private request page in your confirmation email.`);
        this.status.focus();
      } catch (error) {
        this.message(error.name==='AbortError' ? 'We could not confirm your request yet. Retry the saved request to check for its receipt.' : error.message || 'We could not confirm your request. Please retry.',true);
        this.retry.hidden=!this.pending;
        this.status.focus();
      } finally {clearTimeout(timeout);this.busy=false;}
    }
  });
})();
